import React, { useMemo, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import LockOutline from '@material-ui/icons/LockOutlined';
import { translate } from 'components/Translation';
import CustomPasswordInput from 'components/CustomInput/CustomPasswordInput';
import Button from 'components/CustomButtons/Button';
import CustomLink from 'components/CustomLink/CustomLink';
import styles from 'assets/jss/material-dashboard-pro-react/views/password/passwordFormStyles';
import { BodyM, Danger } from 'components/Typography/Typography';
import PasswordRules from './PasswordRules';
import { useSelector } from 'react-redux';
import { getCommonLabels } from 'redux/modules/dynamicLabels/selectors';

// @ts-ignore
const useStyles = makeStyles(styles);

interface FormData {
	password: string;
	repeat_password: string;
}

interface IPasswordFormProps {
	formData: FormData;
	onChange: (name: keyof FormData, value: string) => void;
	onSubmit: () => void;
}

const PasswordForm = (props: IPasswordFormProps) => {
	const { formData, onChange, onSubmit } = props;
	const [showErrors, setShowErrors] = useState(false);
	const [rulePassed, setRulePassed] = useState(false);

	const commonLabels = useSelector(getCommonLabels);

	const errorMessage = useMemo(() => {
		let errorMessage = '';

		if (
			formData.password.length > 0 &&
			formData.repeat_password.length > 0 &&
			formData.password !== formData.repeat_password
		) {
			errorMessage = commonLabels.passwordsNotMatched;
		}

		return errorMessage;
	}, [formData, commonLabels]);

	const isValid =
		Object.keys(formData).every((key) => formData[key].length > 0) &&
		rulePassed &&
		formData.password === formData.repeat_password;

	const buttonDisabled = !isValid;

	const submit = (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		if (rulePassed) {
			onSubmit();
		} else {
			setShowErrors(true);
		}
	};

	const classes = useStyles();
	return (
		<div>
			<form onSubmit={submit}>
				<CustomPasswordInput
					labelText={translate('number.settings.new.password')}
					id="password"
					formControlProps={{
						fullWidth: true,
					}}
					error={!isValid && showErrors}
					icon={<LockOutline />}
					inputProps={{
						onChange: (event: React.FormEvent<HTMLInputElement>) =>
							onChange('password', (event.target as HTMLInputElement).value),
						required: true,
						value: formData.password,
						type: 'password',
						autoComplete: 'off',
					}}
				/>
				<CustomPasswordInput
					labelText={translate('number.settings.confirm.new.password')}
					id="repeat_password"
					formControlProps={{
						fullWidth: true,
					}}
					error={!isValid && showErrors}
					icon={<LockOutline />}
					inputProps={{
						onChange: (event: React.FormEvent<HTMLInputElement>) =>
							onChange('repeat_password', (event.target as HTMLInputElement).value),
						value: formData.repeat_password,
						required: true,
						type: 'password',
						autoComplete: 'off',
					}}
				/>
				{errorMessage && <Danger>{errorMessage}</Danger>}
				<PasswordRules showErrors={showErrors} password={formData.password} setRulePassed={setRulePassed} />
				<div className={classes.buttonContainer}>
					<Button color="primary-light" disabled={buttonDisabled} type="submit">
						{translate('passwordForm.button')}
					</Button>
				</div>
			</form>
			<div className={classes.logIn}>
				<BodyM color="secondary-light">
					<>
						{translate('set.password.already.have.account')}
						<CustomLink to="/auth/login-page" color="brand" type="bodyMBold">
							{translate('set.password.link')}
						</CustomLink>
					</>
				</BodyM>
			</div>
		</div>
	);
};

export default PasswordForm;
